import { Trans } from '@lingui/react/macro';

import {
  type CanaryCompanyVisibilityScore,
  useCanaryVisibilityScoreByCompany,
} from '@/api/aiVisibility';
import { Empty } from '@/components/Empty';
import WidgetError from '@/components/errors/WidgetError';
import DeltaLabel from '@/components/kpi/DeltaLabel';
import LetteredAvatar from '@/components/LetteredAvatar';
import { Panel } from '@/components/panels';
import { Skeleton } from '@/components/Skeleton';
import { numberFormat } from '@/helper/numberFormatter';

const AIVisibilityOverviewCompetitorTable = () => {
  const { scores, isLoading, error } = useCanaryVisibilityScoreByCompany();
  const ranked = [
    ...(scores?.customer ? [scores.customer] : []),
    ...(scores?.competitors ?? []),
  ].sort((a, b) => {
    if (a.overallScore == null) return b.overallScore == null ? 0 : 1;
    if (b.overallScore == null) return -1;
    return b.overallScore - a.overallScore;
  });
  const topFive = ranked.slice(0, 5);
  const preview =
    scores?.customer && !topFive.includes(scores.customer)
      ? [...topFive.slice(0, 4), scores.customer].sort(
          (a, b) => ranked.indexOf(a) - ranked.indexOf(b),
        )
      : topFive;

  return (
    <Panel
      title={<Trans>Competitors</Trans>}
      subtitle={<Trans>Visibility score across all tracked LLMs.</Trans>}
      className="min-h-96"
    >
      {isLoading ? (
        <div className="flex flex-col gap-4 py-2" aria-busy="true">
          {Array.from({ length: 5 }, (_, index) => (
            <Skeleton key={index} className="h-9 w-full" />
          ))}
        </div>
      ) : error ? (
        <WidgetError message={<Trans>Unable to load competitor scores.</Trans>} />
      ) : !preview.length ? (
        <Empty>
          <Trans>No competitor visibility scores available.</Trans>
        </Empty>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-90 text-left text-sm">
            <thead className="sr-only">
              <tr>
                <th scope="col">
                  <Trans>Rank</Trans>
                </th>
                <th scope="col">
                  <Trans>Company</Trans>
                </th>
                <th scope="col">
                  <Trans>7d change</Trans>
                </th>
                <th scope="col">
                  <Trans>Score</Trans>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {preview.map((company: CanaryCompanyVisibilityScore) => (
                <tr
                  key={company.id}
                  className={company === scores?.customer ? 'bg-yellow-50' : undefined}
                >
                  <td className="py-3 pr-2 pl-3 text-gray-500">{ranked.indexOf(company) + 1}</td>
                  <th scope="row" className="py-3 font-medium text-gray-900">
                    <div className="flex items-center gap-2">
                      <LetteredAvatar
                        name={company.company}
                        size="large"
                        className="size-7 shrink-0 text-sm"
                      />
                      <span className="flex min-w-0 flex-col">
                        <span>
                          {company.company}{' '}
                          {company === scores?.customer && (
                            <span className="font-normal text-gray-500">
                              (<Trans>you</Trans>)
                            </span>
                          )}
                        </span>
                        <span className="font-normal text-gray-500">{company.domain}</span>
                      </span>
                    </div>
                  </th>
                  <td className="px-2 py-3 text-right whitespace-nowrap">
                    {company.change == null ? (
                      '—'
                    ) : (
                      <DeltaLabel percentChange={company.change} colorize className="justify-end" />
                    )}
                  </td>
                  <td className="py-3 pr-3 text-right font-semibold text-gray-900">
                    {company.overallScore == null
                      ? '—'
                      : numberFormat(company.overallScore, { precision: 0 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

export default AIVisibilityOverviewCompetitorTable;
